<template>
	<div class="supplier-invoices-page supplier-payments-typography" :class="{ 'is-embedded': embedded }" aria-label="Supplier invoices" @keydown.esc="closeSubmenu">
		<PaymentListFilters :filters="filters" :options="supplierOptions" @change="updateListFilters" />
		<p v-if="actionError" class="error-state" role="alert">{{ actionError }}</p>

		<div class="content-wrapper">
			<div class="table-wrapper">
				<div v-if="showInitialLoading" class="loading-state">Loading supplier invoices…</div>
				<div v-else-if="error" class="error-state">
					{{ error }}
					<button class="link-btn" @click="refresh">Retry</button>
				</div>
				<div v-else class="invoice-list-container payment-list-scroll">
					<table class="invoice-table payment-list-table" aria-label="Subcontractor invoices">
						<thead>
							<tr class="table-header-row">
								<th class="col-verified"><span class="sr-only">Verified</span></th>
								<th class="col-number">Invoice #</th>
								<th class="col-date">Date</th>
								<th class="col-supplier">Supplier</th>
								<th class="col-period">Period</th>
								<th class="col-amount">Amount</th>
								<th class="col-status">Status</th><th class="col-actions"><span class="sr-only">Actions</span></th>
							</tr>
						</thead>
						<tbody>
								<tr v-if="!visibleInvoices.length && !hasInProgressInvoices">
									<td colspan="8" class="empty-cell payment-empty">No supplier invoices found for current filters.</td>
								</tr>
								<!-- An invoice the bot is still reading: what is known now, placeholders for the rest. -->
								<template v-for="item in inProgressInvoices" :key="`reading-${item.id}`">
									<tr class="processing-row">
										<td class="col-verified"></td>
										<td class="col-number"><span class="processing-skeleton" style="width: 34px"></span></td>
										<td class="col-date"><span class="processing-skeleton" style="width: 74px"></span></td>
										<td class="col-supplier" :title="item.sourceEmail ? `From ${item.sourceEmail}` : undefined">
											<span class="processing-file"><svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="3.5" width="12" height="9" rx="1.5" /><path d="M2.5 4.5 8 9l5.5-4.5" /></svg>{{ item.attachmentName || item.sourceSubject || 'Invoice from email' }}</span>
										</td>
										<td class="col-period"><span class="processing-skeleton" style="width: 150px"></span></td>
										<td class="col-amount"><span class="processing-skeleton" style="width: 54px"></span></td>
										<td class="col-status"><span class="processing-status">{{ readingLabel(item) }}</span></td>
										<td class="col-actions"></td>
									</tr>
									<tr class="processing-progress" aria-hidden="true">
										<td colspan="8"><span class="processing-bar" :style="{ transform: `scaleX(${readingProgress(item)})` }"></span></td>
									</tr>
								</template>
								<tr
									v-for="item in visibleInvoices"
									:key="item.id"
									class="table-row payment-list-row"
									:class="{
										active: activeSubmenuItemId === item.id,
										'status-editor-open': statusEditorOpenItemId === item.id,
										'is-fresh': freshInvoiceIds.has(item.id)
									}"
									@click="toggleSubmenu(item, $event)"
								@contextmenu.prevent="openSubmenu(item, $event)"
							>
								<td class="col-verified" @click.stop>
									<button
										type="button"
										class="verify-toggle"
										:class="{ 'is-on': item.isVerified, 'is-loading': verifyingId === item.id }"
										role="switch"
										:aria-checked="Boolean(item.isVerified)"
										:aria-label="`Verified invoice ${item.invoiceNumber || item.id}`"
										:disabled="verifyingId === item.id"
										@click.stop="handleVerifyToggle(item)"
									>
										<span class="verify-toggle-thumb"></span>
									</button>
								</td>
								<td class="col-number">{{ item.invoiceNumber || '—' }}</td>
								<td class="col-date">{{ formatDate(item.invoiceDate) }}</td>
								<td class="col-supplier">
									<span v-if="item.supplier?.id" class="supplier-name" :title="item.supplier.name">{{ item.supplier.name }}</span>
									<button v-else type="button" class="supplier-required" :title="item.supplierNameRaw ? `Invoice name: ${item.supplierNameRaw}` : 'Supplier is not selected'" @click.stop="showInvoiceDetails(item, true)">Select supplier</button>
									<span class="supplier-period-inline">{{ formatPeriod(item) }}</span>
								</td>
								<td class="col-period">{{ formatPeriod(item) }}</td>
									<td class="col-amount" :title="item.supplier?.id ? undefined : 'Select a supplier to show the amount'">{{ item.supplier?.id ? formatCurrency(item.totalAmount) : '—' }}</td>
									<td
										class="col-status status-cell-wrapper"
										:class="{ 'is-open': statusEditorOpenItemId === item.id }"
										@click.stop
									>
										<InvoiceStatusCell use-fixed
											:item-id="item.id"
											:status="resolveStatus(item)"
											:paid-date="item.paidDate"
											:loading="statusUpdatingId === item.id"
											@submit="payload => handleStatusUpdate(item, payload)"
											@open="() => { statusEditorOpenItemId = item.id; closeSubmenu(); }"
											@close="statusEditorOpenItemId = null"
										/>
									</td>
									<td class="col-actions"><button type="button" class="payment-row-more" :aria-label="`Actions for invoice ${item.invoiceNumber || item.id}`" aria-haspopup="true" :aria-expanded="activeSubmenuItemId === item.id" @click.stop="toggleSubmenu(item, $event)"><AppointmentIcon name="more" /></button></td>
								</tr>
						</tbody>
					</table>

					<!-- Keep the row menu outside the horizontal scroll container. -->
					<Teleport to="body"><div
						v-if="activeSubmenuItemId !== null"
						ref="submenuRef" class="submenu supplier-payment-submenu supplier-payments-typography" @click.stop @keydown.esc="closeSubmenu"
						:style="submenuStyle"
					>
						<button
							@click.stop="handleShowSummarySubmenuItem"
							class="submenu-btn"
						>
							Show Summary
						</button>
						<button @click.stop="handlePreviewSubmenuItem" class="submenu-btn">
							Preview invoice
						</button>
						<button @click.stop="handleDownloadSubmenuItem" class="submenu-btn">
							Download PDF
						</button>
						<button @click.stop="handleDeleteSubmenuItem" class="submenu-btn delete">
							Delete Invoice
						</button>
					</div></Teleport>

				</div>
				<div
					v-if="showTableLoadingOverlay"
					class="table-loading-overlay"
					role="status"
					aria-live="polite"
				>
					<div class="table-loading-indicator">
						<span class="table-loading-spinner" aria-hidden="true"></span>
						<span>Loading supplier invoices…</span>
					</div>
				</div>
			</div>

			<div v-if="showFilteredTotalRow" class="payment-filtered-total"><span>{{ visibleInvoices.length }} items</span><strong>Total: {{ formattedFilteredTotal }}</strong></div>
			<PaymentPagination v-if="(pagination.pageCount > 1 || pagination.total) && !hasActiveFilters" v-bind="pagination" :loading="isLoading" @change="changePage" />
		</div>

		<div v-if="showDeleteModal" class="modal-overlay" @click="cancelDelete">
			<div class="modal-content" @click.stop>
				<h3 class="modal-title">Delete Invoice</h3>
				<p class="modal-message">
					Are you sure you want to delete
					{{ deleteInvoiceLabel || 'this invoice' }}?
					This action cannot be undone.
				</p>
				<div class="modal-actions">
					<button type="button" class="modal-btn cancel" @click="cancelDelete">
						Cancel
					</button>
					<button
						type="button" class="modal-btn confirm"
						@click="confirmDelete"
						:disabled="isDeletingInvoice"
					>
						{{ isDeletingInvoice ? 'Deleting...' : 'Delete' }}
					</button>
				</div>
			</div>
		</div>

		<InvoicePreview
			:show-preview="showPreviewModal"
			:pdf-url="previewPdfUrl"
			:invoice-html="previewPdfUrl ? '' : '<p>No PDF preview is available for this invoice.</p>'"
			@close-preview="closePreviewModal"
			@download-pdf="downloadPreviewPdf"
		>
			<template #sidebar>
				<div v-if="previewTarget" class="preview-editor-panel">
					<div class="preview-editor-header">
						<h3 class="preview-editor-title">Invoice details</h3>
						<p class="preview-editor-caption">
							Update the invoice number, invoice date, supplier, and service period.
						</p>
						<p v-if="!previewTarget.supplier?.id && previewTarget.supplierNameRaw" class="preview-editor-caption">Name on invoice: {{ previewTarget.supplierNameRaw }}</p>
						<p v-if="previewShowSummary && previewSummaryRequirement" class="preview-editor-notice" role="status">{{ previewSummaryRequirement }}</p>
						<p v-if="previewError" class="preview-editor-error" role="alert">{{ previewError }}</p>
					</div>

					<div class="preview-editor-group">
						<label class="preview-editor-label" for="preview-invoice-number">Invoice #</label>
						<input
							id="preview-invoice-number"
							v-model="previewInvoiceNumber"
							type="text"
							class="preview-editor-input"
							placeholder="Invoice number"
							:disabled="previewMetadataSaving"
						/>
					</div>

					<div class="preview-editor-group">
						<label class="preview-editor-label">Invoice date</label>
						<DateRangePicker
							:dateFrom="previewInvoiceDate"
							:dateTo="previewInvoiceDate"
							mode="single"
							placeholder="Invoice date"
							:useFixed="true"
							:disabled="previewMetadataSaving"
							@update="handlePreviewInvoiceDateUpdate"
						/>
					</div>

					<div class="preview-editor-group">
						<label class="preview-editor-label">Supplier</label>
						<CatalogSearchInput
							:modelValue="previewSupplierId"
							@update:modelValue="handlePreviewSupplierUpdate"
							:options="supplierOptions"
							placeholder="Search supplier..."
							:disabled="previewMetadataSaving"
						/>
					</div>

					<div class="preview-editor-group">
						<label class="preview-editor-label">Period</label>
						<DateRangePicker
							:dateFrom="previewPeriodFrom"
							:dateTo="previewPeriodTo"
							placeholder="Invoice period"
							:useFixed="true"
							:disabled="previewMetadataSaving"
							@update="handlePreviewPeriodUpdate"
						/>
					</div>

					<div class="preview-editor-actions">
						<button
							type="button"
							class="preview-editor-btn preview-editor-btn--ghost"
							:disabled="previewMetadataSaving || !previewHasChanges"
							@click="resetPreviewMetadataEditor"
						>
							Reset
						</button>
						<button
							type="button"
							class="preview-editor-btn preview-editor-btn--primary"
							:disabled="previewMetadataSaving || !previewHasChanges || (previewShowSummary && Boolean(previewSummaryRequirement))"
							@click="savePreviewMetadata"
						>
							{{ previewMetadataSaving ? 'Saving…' : previewShowSummary ? 'Save and show summary' : 'Save changes' }}
						</button>
					</div>
				</div>
			</template>
		</InvoicePreview>
	</div>
</template>

<script setup>
import { onEvent } from '@/utils/eventBus';
import { computed, nextTick, onActivated, onDeactivated, onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import { useSupplierInvoicesStore } from '@/stores/supplierInvoices';
import { useMediaStore } from '@/stores/media';
import { useCatalogStore } from '@/stores/catalog';
import { useTabsStore } from '@/stores/tabs';
import CatalogSearchInput from '@/components/CatalogSearchInput.vue';
import DateRangePicker from '@/components/DateRangePicker.vue';
import InvoicePreview from '@/components/invoices/InvoicePreview.vue';
import InvoiceStatusCell from '@/components/invoices/InvoiceStatusCell.vue';
import AppointmentsJournalView from '@/views/journal/AppointmentsJournalView.vue';
import { BsCalendar3 } from '@kalimahapps/vue-icons';
import AppointmentIcon from '@/components/icons/AppointmentIcon.vue';
import PaymentListFilters from '@/components/payments/PaymentListFilters.vue';
import PaymentPagination from '@/components/payments/PaymentPagination.vue';
import { fitFloatingPanel } from '@/utils/floatingPanel';
import '@/assets/styles/payment-lists.css';

const props = defineProps({
	embedded: Boolean,
	initialSupplierId: {
		type: [String, Number],
		default: '',
	},
	initialOpenInvoiceId: {
		type: [String, Number],
		default: '',
	},
	initialAutoPreview: {
		type: Boolean,
		default: false,
	},
});

const store = useSupplierInvoicesStore();
const mediaStore = useMediaStore();
const catalogStore = useCatalogStore();
const tabsStore = useTabsStore();
const filters = reactive({
	status: store.filters.status || '',
	supplierId: store.filters.supplierId || '',
	dateFrom: store.filters.dateFrom || '',
	dateTo: store.filters.dateTo || '',
});

const invoices = computed(() => store.invoiceList || []);
const isLoading = computed(() => store.isLoading);
const error = computed(() => store.error);
const hasFetchedInvoices = computed(() => store.hasFetched);
const showInitialLoading = computed(() => isLoading.value && !hasFetchedInvoices.value);
const showTableLoadingOverlay = computed(
	() => isLoading.value && hasFetchedInvoices.value && !error.value,
);
const supplierOptions = computed(() => catalogStore.supplierOptions || []);

const pagination = computed(
	() =>
		store.pagination || {
			page: 1,
			pageCount: 1,
			pageSize: 0,
			total: 0,
		},
);
const activeSubmenuItemId = ref(null);
const submenuRef = ref(null);
const submenuStyle = ref({});
let submenuPoint = { left: 0, top: 0, bottom: 0 };
const showPreviewModal = ref(false);
const previewPdfUrl = ref('');
const previewFileName = ref('');
const previewTarget = ref(null);
const previewInvoiceNumber = ref('');
const previewInvoiceDate = ref('');
const previewSupplierId = ref('');
const previewPeriodFrom = ref('');
const previewPeriodTo = ref('');
const previewMetadataSaving = ref(false);
const previewShowSummary = ref(false);
const previewError = ref('');
const showDeleteModal = ref(false);
const deleteTarget = ref(null);
const isDeletingInvoice = ref(false);
const statusUpdatingId = ref(null);
const statusEditorOpenItemId = ref(null);
const verifyingId = ref(null);
const actionError = ref('');

const formatDate = (value) => {
	if (!value) return '—';
	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) return '—';
	return parsed.toLocaleDateString();
};

const formatCurrency = (value) => {
	if (value === null || value === undefined || value === '') return '—';
	const numeric = Number(value);
	if (Number.isNaN(numeric)) return value;
	return new Intl.NumberFormat('en-GB', {
		style: 'currency',
		currency: 'GBP',
	}).format(numeric);
};

const toNumericAmount = (value) => {
	if (value === null || value === undefined || value === '') return null;
	if (typeof value === 'number') return Number.isFinite(value) ? value : null;
	const raw = String(value).trim();
	if (!raw) return null;
	const cleaned = raw.replace(/[^\d.,-]/g, '');
	if (!cleaned) return null;
	const lastComma = cleaned.lastIndexOf(',');
	const lastDot = cleaned.lastIndexOf('.');
	const sepIndex = Math.max(lastComma, lastDot);

	if (sepIndex !== -1) {
		const intPart = cleaned
			.slice(0, sepIndex)
			.replace(/(?!^)-/g, '')
			.replace(/[^\d-]/g, '');
		const fracPart = cleaned
			.slice(sepIndex + 1)
			.replace(/[^\d]/g, '');
		const composed = fracPart ? `${intPart}.${fracPart}` : intPart;
		const parsed = Number.parseFloat(composed);
		return Number.isFinite(parsed) ? parsed : null;
	}

	const digitsOnly = cleaned.replace(/(?!^)-/g, '').replace(/[^\d-]/g, '');
	const parsed = Number.parseFloat(digitsOnly);
	return Number.isFinite(parsed) ? parsed : null;
};

const isInProgressStatus = (value) =>
	String(value || '').trim().toLowerCase() === 'in_progress';

const resolveStatus = (item) => {
	const status = String(item?.status || '').trim().toLowerCase();
	return status === 'paid' ? 'paid' : 'unpaid';
};

// Invoices the bot is still reading, from their own request and from the list itself. They have
// no supplier or date yet, so they show only while no filter is set.
const inProgressInvoices = computed(() => {
	if (store.hasActiveFilters()) return [];
	const listed = new Map(invoices.value.map((item) => [item?.id, item]));
	const reading = new Map();
	for (const item of [...(store.processingItems || []), ...invoices.value]) {
		if (!item || item.isDeleted || !isInProgressStatus(item.status) || reading.has(item.id)) continue;
		const current = listed.get(item.id);
		if (current && !isInProgressStatus(current.status)) continue;
		reading.set(item.id, item);
	}
	return [...reading.values()];
});
const hasInProgressInvoices = computed(() => inProgressInvoices.value.length > 0);
const visibleInvoices = computed(() =>
	invoices.value.filter((item) => !item?.isDeleted && !isInProgressStatus(item?.status)),
);
// The bot reads an invoice in about 30 s and then saves it; the countdown and the line under
// the row are based on that.
const READING_ESTIMATE_SECONDS = 30;
const readingNow = ref(Date.now());
let readingClock = null;
watch(hasInProgressInvoices, (reading) => {
	window.clearInterval(readingClock);
	readingClock = reading ? window.setInterval(() => { readingNow.value = Date.now(); }, 500) : null;
	readingNow.value = Date.now();
}, { immediate: true });
// The bot stamps receivedAt when it starts on the attachment.
const readingSeconds = (item) => {
	const startedAt = new Date(item?.receivedAt || 0).getTime();
	return startedAt > 0 ? Math.max(0, (readingNow.value - startedAt) / 1000) : 0;
};
// The file is attached once the invoice has been read, while the bot saves it.
const readingProgress = (item) => (item?.file ? 0.97 : Math.min(0.92, (readingSeconds(item) / READING_ESTIMATE_SECONDS) * 0.92));
const readingLabel = (item) => {
	if (item?.file) return 'Saving…';
	const elapsed = readingSeconds(item);
	const left = READING_ESTIMATE_SECONDS - elapsed;
	if (left > 3) return `Reading · ~${left > 12 ? Math.ceil(left / 5) * 5 : Math.ceil(left)} s`;
	return elapsed < 120 ? 'Reading…' : 'Still reading…';
};

// An invoice that was being read lights up briefly once it is ready. Its two sources can update
// a moment apart, so it counts as just read for a minute after it was last seen being read.
const freshInvoiceIds = ref(new Set());
const lastSeenReading = new Map();
watch([inProgressInvoices, visibleInvoices], ([reading, visible]) => {
	const now = Date.now();
	for (const item of reading) lastSeenReading.set(item.id, now);
	for (const item of visible) {
		if (!lastSeenReading.has(item.id) || reading.some((entry) => entry.id === item.id)) continue;
		lastSeenReading.delete(item.id);
		freshInvoiceIds.value = new Set(freshInvoiceIds.value).add(item.id);
		window.setTimeout(() => {
			const next = new Set(freshInvoiceIds.value);
			next.delete(item.id);
			freshInvoiceIds.value = next;
		}, 3000);
	}
	for (const [id, seenAt] of lastSeenReading) {
		if (now - seenAt > 60000) lastSeenReading.delete(id);
	}
}, { immediate: true });

const hasActiveFilters = computed(() =>
	Boolean(
		(filters.status && filters.status !== 'all') ||
		filters.supplierId ||
		filters.dateFrom ||
		filters.dateTo
	),
);

const filteredTotals = computed(() => {
	if (!hasActiveFilters.value) return { total: null, counted: 0 };
	if (visibleInvoices.value.some((item) => !item.supplier?.id)) return { total: null, counted: 0 };
	let total = 0;
	let counted = 0;
	visibleInvoices.value.forEach((item) => {
		const amount = toNumericAmount(item?.totalAmount);
		if (amount === null) return;
		total += amount;
		counted += 1;
	});
	return { total: counted ? total : null, counted };
});

const formattedFilteredTotal = computed(() => formatCurrency(filteredTotals.value.total));

const showFilteredTotalRow = computed(() => hasActiveFilters.value && visibleInvoices.value.length);

const unsubscribePayments = onEvent('supplier-payments:changed', () => { void refresh(); });
onUnmounted(unsubscribePayments);

const handleVerifyToggle = async (item) => {
	if (!item || verifyingId.value !== null) return;
	const previousValue = item.isVerified;
	verifyingId.value = item.id;
	actionError.value = '';
	item.isVerified = !previousValue;
	try {
		const saved = await store.saveInvoiceVerification({
			documentId: item.documentId,
			id: item.id,
			isVerified: item.isVerified,
		});
		if (saved) item.isVerified = saved.isVerified ?? item.isVerified;
	} catch (error) {
		item.isVerified = previousValue;
		actionError.value = error.response?.data?.error?.message || error.message || 'Failed to update invoice verification.';
	} finally {
		verifyingId.value = null;
	}
};

const formatPeriod = (item) => {
	const start = formatDate(item?.servicePeriodStart);
	const end = formatDate(item?.servicePeriodEnd);
	if (start === '—' && end === '—') return '—';
	if (start === '—') return end;
	if (end === '—') return start;
	return `${start} — ${end}`;
};

const handleStatusUpdate = async (item, { status, paidDate }) => {
	if (!item || statusUpdatingId.value !== null) return;
	const nextStatus = status === 'paid' ? 'paid' : 'unpaid';
	const nextPaidDate = nextStatus === 'paid' ? paidDate : null;
	if (item.status === nextStatus && item.paidDate === nextPaidDate) return;
	statusUpdatingId.value = item.id;
	actionError.value = '';
	try {
		await store.saveInvoiceStatus({
			documentId: item.documentId,
			id: item.id,
			status: nextStatus,
			paidDate: nextPaidDate,
		});
	} catch (error) {
		actionError.value = error.response?.data?.error?.message || error.message || 'Failed to update invoice status.';
	} finally {
		statusUpdatingId.value = null;
	}
};

const toISODate = (value) => {
	if (!value) return '';
	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) return '';
	const yyyy = parsed.getFullYear();
	const mm = String(parsed.getMonth() + 1).padStart(2, '0');
	const dd = String(parsed.getDate()).padStart(2, '0');
	return `${yyyy}-${mm}-${dd}`;
};

const normalizePeriodRange = (dateFrom, dateTo) => {
	let start = dateFrom || dateTo || '';
	let end = dateTo || dateFrom || '';
	if (!start && !end) {
		return { dateFrom: '', dateTo: '' };
	}
	if (start && end && start > end) {
		[start, end] = [end, start];
	}
	return { dateFrom: start, dateTo: end };
};

const syncPreviewMetadataEditor = (item) => {
	previewTarget.value = item || null;
	previewInvoiceNumber.value =
		item?.invoiceNumber === null || item?.invoiceNumber === undefined
			? ''
			: String(item.invoiceNumber);
	previewInvoiceDate.value = toISODate(item?.invoiceDate);
	previewSupplierId.value =
		item?.supplier?.id === null || item?.supplier?.id === undefined
			? ''
			: String(item.supplier.id);
	const normalizedPeriod = normalizePeriodRange(
		toISODate(item?.servicePeriodStart),
		toISODate(item?.servicePeriodEnd),
	);
	previewPeriodFrom.value = normalizedPeriod.dateFrom;
	previewPeriodTo.value = normalizedPeriod.dateTo;
};

const resetPreviewMetadataEditor = () => {
	syncPreviewMetadataEditor(previewTarget.value);
	previewError.value = '';
};

const previewSummaryRequirement = computed(() => {
	const missing = [];
	if (!previewSupplierId.value) missing.push('a supplier from the catalog');
	if (!previewPeriodFrom.value && !previewPeriodTo.value) missing.push('the service period');
	return missing.length ? `Select ${missing.join(' and ')} to show this invoice’s summary.` : '';
});

const previewHasChanges = computed(() => {
	if (!previewTarget.value) return false;
	const originalInvoiceNumber =
		previewTarget.value?.invoiceNumber === null || previewTarget.value?.invoiceNumber === undefined
			? ''
			: String(previewTarget.value.invoiceNumber);
	const originalInvoiceDate = toISODate(previewTarget.value?.invoiceDate);
	const originalSupplierId =
		previewTarget.value?.supplier?.id === null || previewTarget.value?.supplier?.id === undefined
			? ''
			: String(previewTarget.value.supplier.id);
	const originalPeriod = normalizePeriodRange(
		toISODate(previewTarget.value?.servicePeriodStart),
		toISODate(previewTarget.value?.servicePeriodEnd),
	);
	return (
		previewInvoiceNumber.value !== originalInvoiceNumber ||
		previewInvoiceDate.value !== originalInvoiceDate ||
		previewSupplierId.value !== originalSupplierId ||
		previewPeriodFrom.value !== originalPeriod.dateFrom ||
		previewPeriodTo.value !== originalPeriod.dateTo
	);
});

const handlePreviewSupplierUpdate = (value) => {
	previewSupplierId.value =
		value === null || value === undefined || value === '' ? '' : String(value);
};

const handlePreviewPeriodUpdate = ({ dateFrom, dateTo }) => {
	const normalized = normalizePeriodRange(dateFrom || '', dateTo || '');
	previewPeriodFrom.value = normalized.dateFrom;
	previewPeriodTo.value = normalized.dateTo;
};

const handlePreviewInvoiceDateUpdate = ({ dateFrom }) => {
	previewInvoiceDate.value = toISODate(dateFrom);
};

const savePreviewMetadata = async () => {
	const item = previewTarget.value;
	if (!item || previewMetadataSaving.value || !previewHasChanges.value) return;
	if (previewShowSummary.value && previewSummaryRequirement.value) return;

	previewMetadataSaving.value = true;
	previewError.value = '';
	const normalizedPeriod = normalizePeriodRange(previewPeriodFrom.value, previewPeriodTo.value);
	const normalizedInvoiceNumber = String(previewInvoiceNumber.value || '').trim();

	try {
		const saved = await store.saveInvoiceMetadata({
			documentId: item.documentId,
			id: item.id,
			invoiceNumber: normalizedInvoiceNumber || null,
			invoiceDate: previewInvoiceDate.value || null,
			supplierId: previewSupplierId.value || null,
			servicePeriodStart: normalizedPeriod.dateFrom || null,
			servicePeriodEnd: normalizedPeriod.dateTo || null,
		});
		if (saved) {
			syncPreviewMetadataEditor(saved);
			if (previewShowSummary.value) {
				if (!saved.supplier?.id || !resolveAppointmentsPeriod(saved)) {
					throw new Error('The saved invoice is missing its supplier or service period. Please try again.');
				}
				previewMetadataSaving.value = false;
				closePreviewModal();
				showInvoiceSummary(saved);
			}
		} else {
			throw new Error('The invoice could not be saved. Please try again.');
		}
	} catch (error) {
		previewError.value = error.response?.data?.error?.message || error.message || 'Failed to save invoice details.';
	} finally {
		previewMetadataSaving.value = false;
	}
};

const applyFilters = () => {
	return store.updateFilters({
		status: filters.status || '',
		supplierId: filters.supplierId || '',
		dateFrom: filters.dateFrom || '',
		dateTo: filters.dateTo || '',
	});
};

const updateListFilters = values => {
	Object.assign(filters, values);
	applyFilters();
};

const refresh = () => store.refresh();

const changePage = (page) => {
	if (hasActiveFilters.value) return;
	store.changePage(page);
};

// Looks for new invoices every 5 s, every 2 s while one is being read, and not while the page
// is out of sight.
const REFRESH_MS = 5000;
const REFRESH_WHILE_READING_MS = 2000;
let refreshTimer = null;
let refreshActive = false;
const scheduleRefresh = () => {
	window.clearTimeout(refreshTimer);
	if (!refreshActive) return;
	refreshTimer = window.setTimeout(async () => {
		try {
			if (document.visibilityState !== 'hidden') await Promise.all([store.fetchNewInvoices(), store.fetchProcessingInvoices()]);
		} finally {
			scheduleRefresh();
		}
	}, hasInProgressInvoices.value ? REFRESH_WHILE_READING_MS : REFRESH_MS);
};
const startRefresh = () => {
	refreshActive = true;
	scheduleRefresh();
};
const stopRefresh = () => {
	refreshActive = false;
	window.clearTimeout(refreshTimer);
	refreshTimer = null;
};

const closeSubmenu = () => {
	activeSubmenuItemId.value = null;
};

const calculateSubmenuPosition = async event => {
	if (event) {
		// Keyboard activation has no pointer position; keep it next to the trigger.
		const trigger = event.detail === 0 && event.clientX === 0 && event.clientY === 0
			? event.currentTarget?.getBoundingClientRect()
			: null;
		submenuPoint = trigger
			? { left: trigger.left, top: trigger.top, bottom: trigger.bottom }
			: { left: event.clientX, top: event.clientY, bottom: event.clientY };
	}
	await nextTick();
	if (!submenuRef.value) return;
	submenuStyle.value = {
		...fitFloatingPanel(submenuPoint, {
			width: submenuRef.value.getBoundingClientRect().width,
			height: submenuRef.value.scrollHeight,
		}),
		width: 'max-content',
	};
};
const handleViewportChange = () => calculateSubmenuPosition();

const toggleSubmenu = (item, event) => {
	if (event) {
		event.stopPropagation();
		if (event.target.closest('.submenu-btn')) {
			return;
		}
	}

	if (activeSubmenuItemId.value === item.id) {
		closeSubmenu();
		return;
	}

	activeSubmenuItemId.value = item.id;
	calculateSubmenuPosition(event);
};

const openSubmenu = (item, event) => {
	if (event) {
		event.preventDefault();
		event.stopPropagation();
		if (event.target.closest('.submenu-btn')) {
			return;
		}
	}

	activeSubmenuItemId.value = item.id;
	calculateSubmenuPosition(event);
};

const findActiveItem = () =>
	invoices.value.find((item) => item.id === activeSubmenuItemId.value);

const resolveAppointmentsPeriod = (item) => {
	const start = toISODate(item?.servicePeriodStart);
	const end = toISODate(item?.servicePeriodEnd);
	if (start && end) {
		return start <= end
			? { dateFrom: start, dateTo: end }
			: { dateFrom: end, dateTo: start };
	}
	if (start) {
		return { dateFrom: start, dateTo: start };
	}
	if (end) {
		return { dateFrom: end, dateTo: end };
	}
	return null;
};

const openAppointmentsJournal = ({
	initialWeekStart,
	initialDateFrom,
	initialDateTo,
	supplierId,
	summaryMode,
	sourceSupplierInvoiceId,
	sourceSupplierInvoiceDocumentId,
} = {}) => {
	const handoffKey = Date.now();
	const summaryModeKey = summaryMode ? handoffKey : null;
	const existing = tabsStore.findMatchingTab({
		component: AppointmentsJournalView,
		section: 'menu',
		tabKey: 'journal:appointments',
	});

	const tabProps = {
		initialWeekStart: initialWeekStart || null,
		initialDateFrom: initialDateFrom || null,
		initialDateTo: initialDateTo || null,
		initialSupplierId: supplierId || '',
		initialSummaryMode: Boolean(summaryMode),
		summaryModeKey,
		handoffKey,
		sourceSupplierInvoiceId: sourceSupplierInvoiceId || null,
		sourceSupplierInvoiceDocumentId: sourceSupplierInvoiceDocumentId || null,
	};

	if (existing) {
		tabsStore.updateTab(existing.id, {
			props: tabProps,
			title: 'Appointments',
			icon: BsCalendar3,
			section: 'menu',
			tabKey: 'journal:appointments',
		});
		tabsStore.activateTab(existing.id);
		return;
	}

	const tab = tabsStore.createTab({
		component: AppointmentsJournalView,
		title: 'Appointments',
		props: tabProps,
		icon: BsCalendar3,
		section: 'menu',
		tabKey: 'journal:appointments',
	});
	if (tab) {
		tabsStore.activateTab(tab.id);
	}
};

const showInvoiceSummary = (item) => {
	const supplierId = item?.supplier?.id;
	const period = resolveAppointmentsPeriod(item);
	if (!supplierId || !period) return false;

	openAppointmentsJournal({
		initialWeekStart: period.dateFrom,
		initialDateFrom: period.dateFrom,
		initialDateTo: period.dateTo,
		supplierId: String(supplierId),
		summaryMode: true,
		sourceSupplierInvoiceId: item?.id,
		sourceSupplierInvoiceDocumentId: item?.documentId,
	});
	return true;
};

const handleShowSummarySubmenuItem = async () => {
	const item = findActiveItem();
	closeSubmenu();
	if (!item) return;
	if (!showInvoiceSummary(item)) await showInvoiceDetails(item, true);
};

const resolveFileUrl = async (item) => {
	const file = item?.file;
	if (!file) return null;
	if (file.id) {
		const signed = await mediaStore.getSignedUrl(file.id);
		return signed?.url || file.url || null;
	}
	return file.url || null;
};

const deriveFileName = (item) => {
	const formatDateForFilename = (value) => {
		if (!value) return '';
		const parsed = new Date(value);
		if (Number.isNaN(parsed.getTime())) return '';
		const dd = String(parsed.getDate()).padStart(2, '0');
		const mm = String(parsed.getMonth() + 1).padStart(2, '0');
		const yyyy = parsed.getFullYear();
		return `${dd}${mm}${yyyy}`;
	};

	const formatAmountForFilename = (value) => {
		if (value === null || value === undefined || value === '') return '';
		const numeric = Number(value);
		if (Number.isNaN(numeric)) return '';
		return `£${numeric.toFixed(2)}`;
	};

	const sanitizeFileNamePart = (value = '') =>
		String(value || '')
			.replace(/[\\/:*?"<>|]/g, ' ')
			.replace(/\s+/g, ' ')
			.trim();

	const dateStr = formatDateForFilename(item?.invoiceDate);
	const amountStr = formatAmountForFilename(item?.totalAmount);
	const supplierName = sanitizeFileNamePart(
		item?.supplier?.name || item?.supplierNameRaw || 'Supplier',
	);

	const parts = [supplierName, 'Invoice'];
	if (dateStr) parts.push(dateStr);
	if (amountStr) parts.push(amountStr);

	return `${parts.join(' ')}.pdf`;
};

const downloadFileUrl = async (url, filename) => {
	if (!url) return;
	try {
		const response = await fetch(url);
		if (!response.ok) {
			throw new Error(`Download failed with status ${response.status}`);
		}
		const blob = await response.blob();
		const objectUrl = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = objectUrl;
		link.download = filename || 'supplier-invoice.pdf';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(objectUrl);
	} catch (error) {
		console.error('Failed to download invoice file', error);
	}
};

const openPreviewModal = (file) => {
	previewPdfUrl.value = file.url || '';
	previewFileName.value = file.name || '';
	syncPreviewMetadataEditor(file.item || null);
	previewShowSummary.value = Boolean(file.showSummary);
	previewError.value = '';
	showPreviewModal.value = true;
};

const closePreviewModal = () => {
	if (previewMetadataSaving.value) return;
	showPreviewModal.value = false;
	previewPdfUrl.value = '';
	previewFileName.value = '';
	previewTarget.value = null;
	previewInvoiceNumber.value = '';
	previewInvoiceDate.value = '';
	previewSupplierId.value = '';
	previewPeriodFrom.value = '';
	previewPeriodTo.value = '';
	previewMetadataSaving.value = false;
	previewShowSummary.value = false;
	previewError.value = '';
};

const showInvoiceDetails = async (item, showSummary = false) => {
	closeSubmenu();
	if (!item) return;
	openPreviewModal({ url: '', name: deriveFileName(item), item, showSummary });
	try {
		const url = await resolveFileUrl(item);
		if (showPreviewModal.value && previewTarget.value === item) previewPdfUrl.value = url || '';
	} catch (error) {
		if (showPreviewModal.value && previewTarget.value === item) previewError.value = 'The PDF preview could not be loaded. You can still update the invoice details.';
	}
};

const handlePreviewSubmenuItem = async () => showInvoiceDetails(findActiveItem());

const handleDownloadSubmenuItem = async () => {
	const item = findActiveItem();
	closeSubmenu();
	if (!item) return;
	try {
		const url = await resolveFileUrl(item);
		if (!url) return;
		await downloadFileUrl(url, deriveFileName(item));
	} catch (error) {
		console.error('Failed to download invoice file', error);
	}
};

const downloadPreviewPdf = async () => {
	if (!previewPdfUrl.value) return;
	await downloadFileUrl(previewPdfUrl.value, previewFileName.value);
};

const handleDeleteSubmenuItem = () => {
	const item = findActiveItem();
	closeSubmenu();
	if (!item) return;
	deleteTarget.value = item;
	showDeleteModal.value = true;
};

const cancelDelete = () => {
	if (isDeletingInvoice.value) return;
	showDeleteModal.value = false;
	deleteTarget.value = null;
};

const deleteInvoiceLabel = computed(() => {
	const target = deleteTarget.value;
	if (!target) return '';
	const number = target.invoiceNumber ? `invoice #${target.invoiceNumber}` : 'this invoice';
	const supplier = target.supplier?.name || target.supplierNameRaw || '';
	return supplier ? `${number} for ${supplier}` : number;
});

const confirmDelete = async () => {
	if (!deleteTarget.value || isDeletingInvoice.value) return;
	isDeletingInvoice.value = true;
	try {
		await store.deleteInvoice({
			documentId: deleteTarget.value.documentId,
			id: deleteTarget.value.id,
		});
		showDeleteModal.value = false;
		deleteTarget.value = null;
	} catch (error) {
		console.error('Failed to delete supplier invoice', error);
	} finally {
		isDeletingInvoice.value = false;
	}
};

const handlePageClick = event => {
	if (!submenuRef.value?.contains(event.target) && (!event.target.closest('.table-row') || event.target.closest('.col-verified, .col-status'))) closeSubmenu();
};
onDeactivated(() => {
	closeSubmenu();
	stopRefresh();
});
// Back on the page (a kept-alive tab): catch up at once, then keep checking.
onActivated(() => {
	if (refreshActive || !hasFetchedInvoices.value) return;
	void store.fetchNewInvoices();
	void store.fetchProcessingInvoices();
	startRefresh();
});

onMounted(async () => {
	document.addEventListener('click', handlePageClick, true);
	window.addEventListener('resize', handleViewportChange);
	window.visualViewport?.addEventListener('resize', handleViewportChange);
	window.visualViewport?.addEventListener('scroll', handleViewportChange);
	void catalogStore.bootstrap?.();
	const normalize = (value) => (value === null || value === undefined ? '' : String(value));
	const initialSupplier = normalize(props.initialSupplierId);
	const initialOpenKey = normalize(props.initialOpenInvoiceId);

	if (initialSupplier) {
		filters.supplierId = initialSupplier;
		await store.updateFilters({
			status: filters.status || '',
			supplierId: initialSupplier,
			dateFrom: filters.dateFrom || '',
			dateTo: filters.dateTo || '',
		});
	} else {
		if (!hasFetchedInvoices.value) {
			await store.fetchInvoices();
		}
	}

	const tryAutoOpen = async () => {
		if (!initialOpenKey) return;
		const fromList =
			invoices.value.find(
				(item) => String(item?.documentId ?? item?.id ?? '') === String(initialOpenKey),
			) || null;
		const resolved = fromList || (await store.fetchInvoiceByIdOrDocumentId(initialOpenKey));
		if (!resolved || !props.initialAutoPreview) return;
		try {
			const url = await resolveFileUrl(resolved);
			if (!url) return;
			openPreviewModal({ url, name: deriveFileName(resolved), item: resolved });
		} catch (error) {
			console.error('Failed to preview invoice file', error);
		}
	};

	await nextTick();
	await tryAutoOpen();

	void store.fetchProcessingInvoices();
	startRefresh();
});

onUnmounted(() => {
	document.removeEventListener('click', handlePageClick, true);
	window.removeEventListener('resize', handleViewportChange);
	window.visualViewport?.removeEventListener('resize', handleViewportChange);
	window.visualViewport?.removeEventListener('scroll', handleViewportChange);
	stopRefresh();
	window.clearInterval(readingClock);
});
</script>

<style scoped>
.supplier-invoices-page { display: flex; flex-direction: column; gap: 16px; padding: 24px; min-width: 0; color: var(--text-color); font: var(--ap-type-body); }
.supplier-invoices-page.is-embedded { padding: 0; }
.content-wrapper { display: flex; flex-direction: column; min-width: 0; }
.table-wrapper { position: relative; min-width: 0; background: transparent; border: 0; border-radius: 0; box-shadow: none; }
.invoice-table { table-layout: fixed; }
.col-verified { width: 60px; }
.col-number { width: 120px; font-weight: 600; }
.col-date { width: 120px; }
.col-supplier { width: auto; }
.col-period { width: 240px; }
.col-amount { width: 130px; }
.col-status { width: 120px; }
.col-actions { width: 48px; }
.invoice-table :is(th, td).col-amount { text-align: right; font-variant-numeric: tabular-nums; }
.invoice-table td.col-amount { font-weight: 600; }
.invoice-table :is(th, td).col-status { text-align: center; }
.invoice-table :is(th, td).col-verified { padding-inline: 16px 8px; text-align: left; }
.invoice-table :is(th, td).col-actions { padding-inline: 4px; text-align: center; }
/* Compact rows on every screen; an iPad shows the same table as a desktop. */
.invoice-table .table-row { height: 38px; }
.invoice-table .table-row > td { padding-block: 1px; }
.invoice-table .col-status :deep(.status-trigger) { min-height: 32px; padding-block: 0; }
/* One line, like the client names in Client Invoices; the full name shows on hover. */
.invoice-table td.col-supplier { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.supplier-required { border: 0; padding: 0; background: transparent; color: var(--noc-blue); font: inherit; cursor: pointer; }
.preview-editor-notice { margin: 0; font: inherit; color: var(--text-color-secondary); }
.preview-editor-error { margin: 0; color: #b42318; }
.supplier-period-inline { display: none; }
/* An invoice the bot is still reading, with a progress line in a 2px row of its own. */
.invoice-table .processing-row { height: 38px; }
.invoice-table .processing-row > td { padding-block: 1px; border-bottom: 0; border-image: none; }
.invoice-table .processing-row > td.col-status { padding-inline: 8px; }
.processing-skeleton {
	display: inline-block; height: 10px; border-radius: 5px; vertical-align: middle;
	background: linear-gradient(90deg, var(--noc-grey-dark) 0%, var(--noc-grey-light) 50%, var(--noc-grey-dark) 100%);
	background-size: 240% 100%; animation: processing-shine 1.6s ease-in-out infinite;
}
@keyframes processing-shine { from { background-position: 120% 0; } to { background-position: -120% 0; } }
.processing-file { color: var(--text-color-secondary); }
.processing-file svg { margin-right: 6px; vertical-align: -2px; }
.processing-status { color: var(--noc-blue); font: var(--ap-type-caption); font-weight: 500; white-space: nowrap; font-variant-numeric: tabular-nums; }
.invoice-table .processing-progress > td { height: 2px; padding: 0; font-size: 0; line-height: 0; }
.processing-bar { display: block; height: 2px; background: var(--noc-blue); transform-origin: left center; transition: transform 0.5s linear; }
.invoice-table .table-row.is-fresh > td { animation: invoice-ready 2.6s ease-out; }
@keyframes invoice-ready { from { background-color: var(--noc-blue-10); } to { background-color: transparent; } }
@media (prefers-reduced-motion: reduce) {
	.processing-skeleton, .invoice-table .table-row.is-fresh > td { animation: none; }
	.processing-bar { transition: none; }
}
@media (max-width: 650px) { .supplier-invoices-page:not(.is-embedded) { padding: 12px; } }
.preview-editor-panel {
	display: flex;
	flex-direction: column;
	gap: 18px;
	min-width: 0;
}

.preview-editor-header {
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.preview-editor-title {
	margin: 0;
	font-size: 1.05rem;
	font-weight: 600;
	color: var(--text-color, #111827);
}

.preview-editor-caption {
	margin: 0;
	font-size: 0.86rem;
	line-height: 1.45;
	color: var(--text-color-secondary, #6b7280);
}

.preview-editor-group {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.preview-editor-meta {
	display: flex;
	flex-direction: column;
	gap: 10px;
	padding: 14px 16px;
	border: 1px solid var(--border-color, #e5e7eb);
	border-radius: 10px;
	background: #f9fafb;
}

.preview-editor-meta-row {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.preview-editor-meta-label {
	font-size: 0.76rem;
	font-weight: 700;
	letter-spacing: 0.03em;
	text-transform: uppercase;
	color: var(--text-color-secondary, #6b7280);
}

.preview-editor-meta-value {
	font-size: 0.95rem;
	font-weight: 600;
	color: var(--text-color, #111827);
}

.preview-editor-label {
	font-size: 0.82rem;
	font-weight: 600;
	color: var(--text-color-secondary, #4b5563);
}

.preview-editor-input,
.preview-editor-group :deep(.field-input),
.preview-editor-group :deep(.drp-trigger) {
	width: 100%;
	min-height: 40px;
	border-radius: 8px;
	border: 1px solid var(--border-color, #d1d5db);
	box-shadow: none;
	background: #fff;
}

.preview-editor-input {
	padding: 0 12px;
	font-size: 0.95rem;
	font-weight: 500;
	color: var(--text-color, #111827);
}

.preview-editor-input::placeholder {
	color: var(--text-color-secondary, #9ca3af);
}

.preview-editor-group :deep(.drp-trigger) {
	justify-content: flex-start;
	padding: 0 12px;
}

.preview-editor-group :deep(.dropdown-menu),
.preview-editor-group :deep(.drp-dropdown) {
	z-index: 1100;
}

.preview-editor-actions {
	display: flex;
	justify-content: flex-end;
	gap: 10px;
	margin-top: 4px;
}

.preview-editor-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	height: 34px;
	padding: 0 12px;
	border-radius: 8px;
	border: 1px solid var(--noc-blue, #0065ff);
	font: inherit;
	font-size: 0.85rem;
	font-weight: 600;
	cursor: pointer;
	transition: filter 0.15s ease, opacity 0.15s ease;
}

.preview-editor-btn:disabled {
	cursor: default;
	opacity: 0.55;
}

.preview-editor-btn--ghost {
	background: #fff;
	color: var(--noc-blue, #0065ff);
}

.preview-editor-btn--primary {
	background: var(--noc-blue, #0065ff);
	color: #fff;
}


.verify-toggle {
	position: relative;
	width: 30px;
	height: 18px;
	border-radius: 3px;
	border: none;
	background: #78788033;
	cursor: pointer;
	padding: 0;
	transition: background-color 0.25s ease;
}

.verify-toggle-thumb {
	position: absolute;
	top: 0;
	left: 0;
	width: 15px;
	height: 18px;
	border-radius: 3px;
	background: #fff;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15), 0 1px 1px rgba(0, 0, 0, 0.1);
	transition: transform 0.25s ease;
}

.verify-toggle.is-on {
	background: #007aff;
}

.verify-toggle.is-on .verify-toggle-thumb {
	transform: translateX(15px);
}

.verify-toggle:focus-visible {
	outline: 2px solid #007aff;
	outline-offset: 2px;
}

.verify-toggle:disabled,
.verify-toggle.is-loading {
	cursor: pointer;
	opacity: 0.5;
}

.col-status :deep(.status-cell) { min-width: 0; align-items: center; }
.col-status :deep(.status-meta) { position: static; transform: none; }

.empty-cell {
	text-align: center;
	color: var(--text-color-secondary);
	padding: 32px;
}

.loading-state,
.error-state {
	padding: 32px;
	text-align: center;
	color: var(--text-color-secondary);
}

.table-loading-overlay {
	position: absolute;
	inset: 0;
	background: rgba(255, 255, 255, 0.82);
	z-index: 20;
	pointer-events: auto;
}

.table-loading-indicator {
	position: absolute;
	top: 10px;
	left: 50%;
	transform: translateX(-50%);
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 6px 10px;
	border-radius: 8px;
	border: 1px solid var(--border-color, #dcdcdc);
	background: rgba(255, 255, 255, 0.96);
	color: var(--text-color-secondary, #4b5563);
	font-size: 0.78rem;
	font-weight: 600;
	letter-spacing: 0.01em;
}

.table-loading-spinner {
	width: 14px;
	height: 14px;
	border-radius: 50%;
	border: 2px solid #d3d7df;
	border-top-color: var(--noc-blue, #0065ff);
	animation: table-loading-spin 0.6s linear infinite;
}

@keyframes table-loading-spin {
	to {
		transform: rotate(360deg);
	}
}

.link-btn {
	border: none;
	background: none;
	color: var(--noc-blue);
	cursor: pointer;
	text-decoration: underline;
	margin-left: 8px;
}

/* Delete confirmation modal */
.modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000;
}

.modal-content {
	background: var(--noc-background);
	border-radius: 8px;
	border: 1px solid var(--border-color);
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
	padding: 24px;
	min-width: 320px;
	max-width: 90%;
	display: flex;
	flex-direction: column;
	gap: 16px;
}

.modal-title {
	margin: 0;
	font-size: 1.25rem;
	font-weight: 600;
	color: var(--text-color);
}

.modal-message {
	margin: 0;
	font-size: 0.95rem;
	color: var(--text-color-secondary);
	line-height: 1.5;
}

.modal-actions {
	display: flex;
	gap: 12px;
	justify-content: flex-end;
	margin-top: 8px;
}

.modal-btn {
	min-height: 34px;
	padding: 0 12px;
	border-radius: 8px;
	border: none;
	font-weight: 600;
	cursor: pointer;
	transition: opacity 0.15s ease, background 0.15s ease;
	font-size: 0.8rem;
}

.modal-btn.cancel {
	background: transparent;
	color: var(--text-color-secondary);
	border: 1px solid var(--border-color);
}

.modal-btn.cancel:hover {
	background: var(--noc-background-secondary);
	color: var(--text-color);
}

.modal-btn.confirm {
	background: var(--noc-red);
	color: #fff;
}

.modal-btn.confirm:hover:not(:disabled) {
	background: var(--noc-red-80);
}

.modal-btn.confirm:disabled {
	opacity: 1;
}
</style>
